import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';

import { ShoppingItemWithProduct } from '@/types';
import { safeToastError } from '@/components/ui/toastManager';
import { isCapacitorPlatform } from '@/utils/platform';

interface ExportShoppingListParams {
  listName: string;
  items: ShoppingItemWithProduct[];
}

const buildProductLabel = (item: ShoppingItemWithProduct): string => {
  const name = item.product?.name || item.name || item.product_id || 'Prodotto';
  const quantity = item.quantity?.trim();
  const unit = item.unit?.trim();

  if (!quantity) {
    return name;
  }

  const unitLabel = unit ? ` ${unit}` : '';
  return `${name} (${quantity}${unitLabel})`;
};

const formatItemsForShare = (items: ShoppingItemWithProduct[]): string =>
  items.map(buildProductLabel).join('\n');

export interface ShareFallbackPayload {
  title: string;
  text: string;
}

export type ExportShoppingListResult =
  | { status: 'shared' }
  | { status: 'fallback'; payload: ShareFallbackPayload }
  | { status: 'error' };

export const exportShoppingList = async ({
  listName,
  items,
}: ExportShoppingListParams): Promise<ExportShoppingListResult> => {
  if (!items.length) {
    safeToastError('Nessun prodotto da esportare');
    return { status: 'error' };
  }

  const text = formatItemsForShare(items);
  const title = listName || 'Lista della spesa';
  const payload: ShareFallbackPayload = { title, text };

  try {
    if (isCapacitorPlatform() && Capacitor.isPluginAvailable('Share')) {
      await Share.share({
        title,
        text,
        dialogTitle: `Esporta ${title}`,
      });
      return { status: 'shared' };
    }

    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      await navigator.share({
        title,
        text,
      });
      return { status: 'shared' };
    }

    if (
      typeof navigator !== 'undefined' &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === 'function'
    ) {
      return {
        status: 'fallback',
        payload,
      };
    }

    safeToastError('Condivisione non supportata: usa le opzioni manuali');
    return { status: 'error' };
  } catch (error) {
    console.error('Errore durante l\'esportazione della lista', error);
    safeToastError("Impossibile esportare la lista. Riprova più tardi.");
    return { status: 'error' };
  }
};

export type { ExportShoppingListParams };
